import * as THREE from 'three';

interface EnvironmentalMark {
  mesh: THREE.Mesh;
  createdAt: number;
  lifetime: number;
}

/**
 * Manages persistent environmental effects on the arena floor:
 * scorch marks from fireballs, frost patches from ice shards,
 * and shield ripple waves.
 *
 * Uses a pool of pre-allocated geometries to avoid per-frame allocation.
 */
export class EnvironmentalEffects {
  private scene: THREE.Scene;
  private marks: EnvironmentalMark[] = [];
  private static readonly MAX_MARKS = 12;
  private static readonly MARK_LIFETIME = 8000; // ms

  // Pre-allocated shared geometries
  private static scorchGeometry: THREE.CircleGeometry | null = null;
  private static frostGeometry: THREE.CircleGeometry | null = null;
  private static rippleGeometry: THREE.RingGeometry | null = null;

  // Active ripple animations
  private ripples: { mesh: THREE.Mesh; elapsed: number; animId: number }[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Create shared geometries once
    if (!EnvironmentalEffects.scorchGeometry) {
      EnvironmentalEffects.scorchGeometry = new THREE.CircleGeometry(1.2, 24);
    }
    if (!EnvironmentalEffects.frostGeometry) {
      EnvironmentalEffects.frostGeometry = new THREE.CircleGeometry(1.0, 6);
    }
    if (!EnvironmentalEffects.rippleGeometry) {
      EnvironmentalEffects.rippleGeometry = new THREE.RingGeometry(0.3, 1.5, 32);
    }
  }

  /** Leave a scorch mark on the floor at impact position */
  createScorchMark(position: THREE.Vector3): void {
    this.evictOldest();

    const material = new THREE.MeshBasicMaterial({
      color: 0x1a0800,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(EnvironmentalEffects.scorchGeometry!, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(position.x, 0.02, position.z);
    mesh.scale.setScalar(0.5 + Math.random() * 0.5);
    mesh.rotation.z = Math.random() * Math.PI * 2;

    this.scene.add(mesh);
    this.marks.push({
      mesh,
      createdAt: Date.now(),
      lifetime: EnvironmentalEffects.MARK_LIFETIME,
    });

    // Add subtle ember particles that fade quickly
    this.createEmberBurst(position);
  }

  /** Leave a frost patch on the floor at impact position */
  createFrostPatch(position: THREE.Vector3): void {
    this.evictOldest();

    const material = new THREE.MeshBasicMaterial({
      color: 0x88ccff,
      transparent: true,
      opacity: 0.45,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(EnvironmentalEffects.frostGeometry!, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(position.x, 0.02, position.z);
    mesh.scale.setScalar(0.6 + Math.random() * 0.5);
    mesh.rotation.z = Math.random() * Math.PI * 2;

    this.scene.add(mesh);
    this.marks.push({
      mesh,
      createdAt: Date.now(),
      lifetime: EnvironmentalEffects.MARK_LIFETIME,
    });
  }

  /** Create an expanding ripple wave from shield activation */
  createShieldRipple(position: THREE.Vector3): void {
    const material = new THREE.MeshBasicMaterial({
      color: 0xc9a227,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(EnvironmentalEffects.rippleGeometry!, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(position.x, 0.03, position.z);

    this.scene.add(mesh);

    let elapsed = 0;
    const entry = { mesh, elapsed: 0, animId: 0 };

    const animate = () => {
      elapsed += 0.016;
      entry.elapsed = elapsed;

      mesh.scale.setScalar(1 + elapsed * 4);
      material.opacity = 0.7 * (1 - elapsed / 0.6);

      if (elapsed < 0.6) {
        entry.animId = requestAnimationFrame(animate);
      } else {
        this.scene.remove(mesh);
        material.dispose();
        const idx = this.ripples.indexOf(entry);
        if (idx !== -1) this.ripples.splice(idx, 1);
      }
    };

    entry.animId = requestAnimationFrame(animate);
    this.ripples.push(entry);
  }

  /** Update marks: fade out and remove expired ones */
  update(): void {
    const now = Date.now();
    const toRemove: number[] = [];

    for (let i = 0; i < this.marks.length; i++) {
      const mark = this.marks[i];
      const age = now - mark.createdAt;
      const fadeStart = mark.lifetime * 0.6;

      if (age > mark.lifetime) {
        toRemove.push(i);
        this.scene.remove(mark.mesh);
        (mark.mesh.material as THREE.MeshBasicMaterial).dispose();
      } else if (age > fadeStart) {
        const fadeProgress = (age - fadeStart) / (mark.lifetime - fadeStart);
        (mark.mesh.material as THREE.MeshBasicMaterial).opacity =
          0.6 * (1 - fadeProgress);
      }
    }

    // Remove expired marks in reverse order
    for (let i = toRemove.length - 1; i >= 0; i--) {
      this.marks.splice(toRemove[i], 1);
    }
  }

  private evictOldest(): void {
    while (this.marks.length >= EnvironmentalEffects.MAX_MARKS) {
      const oldest = this.marks.shift()!;
      this.scene.remove(oldest.mesh);
      (oldest.mesh.material as THREE.MeshBasicMaterial).dispose();
    }
  }

  private createEmberBurst(position: THREE.Vector3): void {
    const count = 15;
    const positions = new Float32Array(count * 3);
    const velocities: { x: number; y: number; z: number }[] = [];

    for (let i = 0; i < count; i++) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = 0.1;
      positions[i * 3 + 2] = position.z;
      velocities.push({
        x: (Math.random() - 0.5) * 3,
        y: 1 + Math.random() * 3,
        z: (Math.random() - 0.5) * 3,
      });
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xff6600,
      size: 0.12,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    this.scene.add(points);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;
      const pos = geometry.attributes.position.array as Float32Array;

      for (let i = 0; i < count; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 4 * 0.016; // gravity
      }
      geometry.attributes.position.needsUpdate = true;
      material.opacity = 1 - elapsed / 0.8;

      if (elapsed < 0.8) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(points);
        geometry.dispose();
        material.dispose();
      }
    };

    requestAnimationFrame(animate);
  }

  dispose(): void {
    // Clean up active marks
    for (const mark of this.marks) {
      this.scene.remove(mark.mesh);
      (mark.mesh.material as THREE.MeshBasicMaterial).dispose();
    }
    this.marks = [];

    // Cancel active ripple animations
    for (const ripple of this.ripples) {
      cancelAnimationFrame(ripple.animId);
      this.scene.remove(ripple.mesh);
      (ripple.mesh.material as THREE.Material).dispose();
    }
    this.ripples = [];
  }
}
